import { API_ERRORS } from "~/utils/constants";
import { useAsyncData, useToast, useGtmLayer } from "#imports";
import { useAuthStorage } from "~/composables/useAuthStorage";

export function useAccountClient() {
  const nuxtApp = useNuxtApp();
  const toast = useToast();
  const loading = ref(false);

  const FF_USER_AUTH_ENABLED = useFeatureFlag("userAuthEnabled");

  const { getToken, clearAuth, setProfilePicture } = useAuthStorage();

  const localToken = useState("localToken", () => null);

  const gtmLayer = useGtmLayer();

  const {
    data: user,
    execute: refreshUser,
    status,
  } = useAsyncData(
    "user-data",
    async () => {
      // Skip on server side to avoid breaking cache
      if (import.meta.server) return null;

      if (!FF_USER_AUTH_ENABLED) return null;

      const token = getToken();
      if (!token) return null;

      try {
        const response = await nuxtApp.$api("/api/account/get");
        if (response?.code === API_ERRORS.API_TOKEN_FAILED) {
          return null;
        }
        gtmLayer.trackUser(response?.accountid ?? null);

        // Update profile picture cookie when user data is available
        if (response) {
          if (response.profile_picture) {
            try {
              new URL(response.profile_picture);
              setProfilePicture(response.profile_picture);
            } catch {
              console.warn(
                "Invalid profile picture URL:",
                response.profile_picture,
              );
              setProfilePicture(null);
            }
          } else {
            setProfilePicture(null);
          }
        }

        return response ?? null;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    {
      server: false, // Disable SSR for user data
      lazy: true,
      default: () => null,
    },
  );

  const getUser = async () => {
    if (!FF_USER_AUTH_ENABLED) return null;

    // Skip on server
    if (import.meta.server) return null;

    const token = getToken();
    if (user.value === null && token) {
      await refreshUser();
    }
    setLocalTokenByCookie();
    return user.value;
  };

  const login = async ({ email, password }) => {
    if (!FF_USER_AUTH_ENABLED) return null;
    loading.value = true;
    try {
      const response = await nuxtApp.$api("/api/account/authorize", {
        method: "POST",
        body: { email, password },
      });
      if (
        response?.statuscode === 500 &&
        response?.code !== API_ERRORS.E_API_ALREADY_AUTHORIZED
      ) {
        throw new Error();
      }
      await refreshUser();
      return response ?? null;
    } catch (e) {
      toast.add({
        title: "Chyba",
        text: "Něco se pokazilo. Zkuste to znovu později.",
        variant: "warning",
      });
      return null;
    } finally {
      loading.value = false;
    }
  };

  const loginByProvider = async ({ provider, provider_data }) => {
    if (!FF_USER_AUTH_ENABLED) return null;
    if (loading.value) return;
    loading.value = true;
    try {
      const response = await nuxtApp.$api("/api/account/authorize", {
        method: "POST",
        body: { provider, provider_data },
      });
      if (
        response?.statuscode === 500 &&
        response?.code !== API_ERRORS.E_API_ALREADY_AUTHORIZED
      ) {
        throw new Error();
      }
      await refreshUser();
      return response ?? null;
    } catch (e) {
      toast.add({
        title: "Chyba",
        text: "Něco se pokazilo. Zkuste to znovu později.",
        variant: "warning",
      });
      return null;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    if (!FF_USER_AUTH_ENABLED) return null;
    try {
      await nuxtApp.$api("/api/account/deauthorize");
      user.value = null;
      clearAuth();
      return null;
    } catch (e) {
      toast.add({
        title: "Chyba",
        text: "Něco se pokazilo. Zkuste to znovu později.",
        variant: "warning",
      });
      user.value = null;
      clearAuth();
      return null;
    }
  };

  const _setLocalToken = (token) => {
    localToken.value = token;
  };

  const setLocalTokenByCookie = () => {
    const token = getToken();
    _setLocalToken(token);
  };

  const isTokenValid = () => {
    if (!FF_USER_AUTH_ENABLED) return true;
    const token = getToken();
    if (!localToken.value || !token) return false;
    return localToken.value === token;
  };

  return {
    loading: computed(() => loading.value || status.value === "pending"),
    user,
    userReady: computed(
      () => status.value !== "pending" && status.value !== "idle",
    ),
    getUser,
    login,
    loginByProvider,
    logout,
    setLocalTokenByCookie,
    isTokenValid,
  };
}
